import { styled } from '@mui/material/styles';
import { Box, Typography, InputBase } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { MEDIA, thinScrollbar, ultraThinScrollbar } from '../../../../../constants'

// Centralized Color Constants
//
// The View/Filter/Slice-specific tokens (chip text/border/svg colors) now
// live in `chips/style.js` as CHIP_COLORS, used by the shared <Chip />
// component. The *LightBg values below are kept because FieldChipWrapper
// still uses them for its own hover background (independent of the chip).
export const COLORS = {
  // Brand & Accent Colors
  primaryLightBg: '#EEF2FF',
  filterLightBg: '#EEF2FF',
  sliceLightBg: '#FDF2F8',

  // SVG Specific Colors
  svgInfoFill: '#4A5056',

  // Neutrals & Base
  white: '#ffffff',
  greyBg: '#f5f5f5',
  cardHoverBg: '#FBFBFF',
  cardHoverBorder: 'rgba(193, 190, 255, 0.50)',

  // Text Colors
  textDark: '#111827',
  textMedium: '#374151',
  textDisabled: '#636B73',
  textMuted: '#8e95a0',
  textNormalVar: '',
  textLightVar: '',

  // Borders & Separators
  separator: '#E0E0DE',
  borderLight: '#E0E0DE',
  borderDefault: '#d3d3d3',
  borderAccordion: '#e0e0e0',

  // Icons & Handles
  iconGrey: '#4A5056',
  gripHandle: '#9ca3af',
  bulletDefault: '#888',
  iconVar: '',

  // Shadows & Overlays
  shadowDefault: 'rgba(0, 0, 0, 0.14)',
  shadowPrimaryHover: 'rgba(99, 102, 241, 0.12)',
  shadowAccordionHover: 'rgba(79, 70, 229, 0.25)',
  bgActiveGroupVar: '#f5f5f5',
};

export const PanelContainer = styled(Box)(`
  background-color: ${COLORS.white};
  border: 0.5px solid ${COLORS.separator};
  border-top: 0;
  border-radius: 0 0 0 10px;
  width: 268px;
  flex-shrink: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;

  @media (max-width: 900px) {
    width: 100%;
    max-height: 260px;
    border-radius: 0;
  }

  /* Styling for items already dropped in canvas */
  .disabled,
  .FieldChipWrapper.disabled {
    color: ${COLORS.textDisabled};
    cursor: not-allowed !important;
    pointer-events: none;
  }

  /* Styling for items disabled by default when View is empty */
  .disabledByDefault,
  .FieldChipWrapper.disabledByDefault {
    color: ${COLORS.textDisabled};
    cursor: not-allowed !important;
  }
`);

export const SearchWrap = styled(Box)(`
  padding: 7.5px 11.5px;
  background-color: ${COLORS.white};
  z-index: 2;
  flex-shrink: 0;
  position: relative;
`);

export const SearchTitle = styled(Typography)(`
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: ${COLORS.textNormalVar};
  padding-top: 4px;
  padding-bottom: 4px;
`);

/* Info Icon Hover Wrapper */
export const InfoWrapper = styled(Box)`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;

  &:hover .info-action-tooltip {
    display: flex;
  }
`;

export const FieldInfo = styled(Box)(`
  width: 20px;
  height: 20px;
  cursor: pointer;
  user-select: none;
`);

export const SearchInputBox = styled(Box)(`
  border: 1px solid ${COLORS.borderLight};
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px 2px 28px;
  width: 100%;
  height: 36px;
  margin: -1px 0 6px 0;
  position: relative;

  & > div {
    line-height: 37px;
  }

  &:before {
    content: '';
    position: absolute;
    top: 10px;
    left: 8px;
    width: 11px;
    height: 11px;
    border: 1px ${COLORS.borderDefault} solid;
    border-radius: 50%;
  }

  &:after {
    background: ${COLORS.borderDefault};
    content: '';
    position: absolute;
    top: 19px;
    left: 17px;
    width: 5px;
    height: 1px;
    transform: rotate(45deg);
  }
`);

export const StyledInputBase = styled(InputBase)(`
  font-size: 13px;
  color: ${COLORS.textLightVar};
  width: 100%;
  font-family: inherit;
`);

export const FieldsList = styled(Box)(`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 11.5px 16px 11.5px;
  overflow-y: auto;
  flex-grow: 1;


  position: relative;
  overflow-y: scroll;
  scroll-behavior: smooth;
  padding-bottom: 140px;

  ${ultraThinScrollbar.styles};
`);

export const SubItemsContainer = styled(Box)(`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-left: 12px;
  padding-left: 16px;
  border-left: 1px solid ${COLORS.borderAccordion};
  margin-top: 2px;
  margin-bottom: 4px;
`);

export const FieldChipWrapper = styled(Box)( ({ type }) => `
  position: relative;
  width: 100%;
  font-weight: 400;

  /* Keep tooltip open when hovering over non-selected items */
  &:hover .hover-action-tooltip,
  &:has(.hover-action-tooltip:hover) .hover-action-tooltip {
    display: flex;
  }

  /* Keep Parent Card Active state only when NOT selected */
  &:hover > button:not(:disabled),
  &:has(.hover-action-tooltip:hover) > button:not(:disabled) {
    ${type === 'view' && `background-color: ${COLORS.primaryLightBg};`}
    ${type === 'filter' && `background-color: ${COLORS.filterLightBg};`}
    ${type === 'slice' && `background-color: ${COLORS.sliceLightBg};`}

    border-color: transparent;
    box-shadow: 0px 4px 16px ${COLORS.shadowPrimaryHover};
    font-weight: 400;
    color: ${COLORS.textDark};
    padding-left: 12px;

    border-radius: 4px;
    // border-bottom: 1px solid ${COLORS.cardHoverBorder};
    // background: ${COLORS.cardHoverBg};
    // box-shadow: -2px 2px 8px 6px ${COLORS.shadowAccordionHover};
  }

  /* Reveal drag handle on hover for non-selected items */
  &:hover > button:not(:disabled)::before,
  &:has(.hover-action-tooltip:hover) > button:not(:disabled)::before {
    opacity: 1;
    width: 12px;
    margin-right: 6px;
  }
`);

export const FieldChip = styled('button')(
  (props) => `
    display: flex;
    align-items: center;
    padding: ${props.isLevel2 ? '8px 12px' : '9px 12px'};
    width: 100%;
    background-color: transparent;
    border-radius: 10px;
    font-size: ${props.isLevel2 ? '13px' : '15px'};
    font-weight: 500;
    line-height: 22px;
    color: ${props.selected ? COLORS.textMuted : COLORS.textMedium};
    cursor: ${props.selected ? 'not-allowed' : 'grab'};
    transition: background-color 0.2s ease, box-shadow 0.2s ease, color 0.15s ease;
    border: 1px solid transparent;
    text-align: left;
    font-family: inherit;
    position: relative;
    line-height: 19px;

    /* Grip handle */
    &::before {
      content: "⠿";
      font-size: 13px;
      color: ${COLORS.gripHandle};
      opacity: 0;
      width: 0;
      margin-right: 0;
      transition: opacity 0.2s ease, width 0.2s ease, margin-right 0.2s ease;
      display: ${props.selected ? 'none' : 'inline-block'};
      line-height: 1;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.65;
      background-color: transparent;
    }

    &.lhsAccordion:before { display:none; }
  `
);

/* Accordion Specific Styled Components */
export const AccordionChip = styled(FieldChip)(
  ({ isExpanded }) => `
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    background-color: ${isExpanded ? COLORS.bgActiveGroupVar : 'transparent'};
    padding: 6px 12px;
    border-radius: 4px;
    width: 100%;
  `
);

export const AccordionLeftGroup = styled(Box)(`
  display: flex;
  align-items: center;
  gap: 8px;
`);

export const AccordionBullet = styled(Box)(`
  height: 6px;
  width: 6px;
  border-radius: 50%;
  background-color: ${COLORS.bulletDefault};
`);

export const AccordionLabel = styled(Typography)(`
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS.textNormalVar};
`);

export const StyledRemoveIcon = styled(RemoveIcon)(`
  font-size: 16px;
  color: ${COLORS.iconVar};
`);

export const StyledAddIcon = styled(AddIcon)(`
  font-size: 16px;
  color: ${COLORS.iconVar};
`);

export const ActionTooltip = styled(Box)( (props) => `
  display: none;
  flex-direction: column;
  gap: 6px;
  position: absolute;
  background: ${COLORS.white};
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0px 8px 24px ${COLORS.shadowDefault};
  z-index: 50;
  ${props.isInfoMenu ? `
    width: 305px;
    left: -10px;
    top: calc(100% + 2px);
    
    ` : `
    width: 105px;
    right: 8px;
    top: calc(100% - 2px);
  
  `}
  pointer-events: auto;

  &::before {
    content: '';
    position: absolute;
    top: -4px;
    ${props.isInfoMenu ? `left: 20px;` : `right: 22px;`}
    width: 9px;
    height: 9px;
    background: ${COLORS.white};
    transform: rotate(45deg);
    border-radius: 2px 0 0 0;
  }
`);

// NOTE: the View / Filter / Slice chip ("ActionPill") and its icons now
// live in the shared `chips/` module (see `Chip` from '../chips') and are
// no longer defined here.

